import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Info } from 'lucide-react';
import { parse } from '@fint/money';
import { api, qs } from '@/lib/api';
import { useActiveOrg } from '@/stores/auth';
import {
  Card, CardContent, Donut, EmptyChart, ErrorState, HBars, LineChart, Money,
  moneyText, tickText, type DonutSlice,
} from '@/features/common/ui';
import { Meter, SeriesChart, Waterfall } from '@/features/common/ui/charts';
import { formatDate } from '@/lib/dates';
import { Tile } from '@/features/dashboard/shared';
import { AdviceStrip, type Advice } from './AdviceStrip';
import type { DebtView, DueView } from './types';
import { usePermissions } from '@/hooks/usePermissions';
import { useFiscalYearStartMonth } from '@/hooks/useOrgSettings';

type Bucket = 'current' | 'd1_30' | 'd31_60' | 'd61_90' | 'd90_plus';

export interface CfoSummary {
  currency: string;
  asOf: string;
  cash: null | {
    total: string;
    cardsOwed: string;
    accounts: Array<{ id: string; name: string; type: string; balance: string }>;
    alsoHeld: Array<{ id: string; name: string; currency: string; balance: string }>;
    /** FDs, funds, chits - beside cash, never in it. Optional while the API rolls out. */
    investments?: { total: string; accounts: Array<{ id: string; name: string; type: string; balance: string }> };
  };
  flows: null | { months: string[]; in: string[]; out: string[] };
  runway: null | { ifNothingComesIn: number | null; atThisPace: number | null; burning: boolean; monthlyOut: string; monthlyIn: string };
  forecast: null | { points: Array<{ date: string; expected: string; cautious: string }>; assumptions: string[] };
  waterfall: null | { month: string; opening: string; steps: Array<{ key: string; label: string; amount: string }>; closing: string };
  receivables: {
    total: string;
    overdue: string;
    count: number;
    overdueCount: number;
    buckets: Record<Bucket, string>;
    customers: Array<{ customerId: string; name: string; total: string; overdue: string; lateDays: number | null }>;
    late: Array<{ id: string; number: string; customer: string; dueDate: string | null; daysLate: number; balance: string }>;
  };
  collection: { months: string[]; days: Array<number | null> };
  concentration: { monthsWithIncome: number; total: string; customers: Array<{ customerId: string | null; name: string; amount: string; share: number }> };
  exports: Array<{ id: string; number: string; customer: string; dueDate: string; daysLeft: number; balance: string; currency: string }>;
  edf: Array<{ month: string; dueDate: string; daysLeft: number; invoiceCount: number }>;
  advice: Advice[];
  dues: DueView[];
}

interface CategoryRow {
  categoryId: string | null;
  name: string;
  colour: string;
  emoji: string | null;
  amount: string;
  share: number;
  excluded?: boolean;
}

const BUCKET_LABEL: Record<Bucket, string> = {
  current: 'Not due yet',
  d1_30: '1-30 days late',
  d31_60: '31-60 days late',
  d61_90: '61-90 days late',
  d90_plus: 'Over 90 days late',
};
const BUCKET_COLOUR: Record<Bucket, string> = { current: 'teal', d1_30: 'amber', d31_60: 'orange', d61_90: 'red', d90_plus: 'red' };
const SLICE_COLOURS = ['violet', 'teal', 'amber', 'blue', 'pink', 'slate'];

/**
 * The Dashboard's CFO mode: the same books, read the way a CFO reads them.
 *
 * ── ORDER ──
 * What to do first (the advice), then the cash position the advice rests
 * on, then the charts that explain both - cash ahead, cash this month, who
 * owes and how late, how fast customers pay, where money comes from and
 * goes. A person who reads only the top of a phone screen gets the actions.
 *
 * Every figure comes from `GET /v1/cfo/summary` or from a report endpoint
 * the Reports screen already uses, so a number here can be found again on
 * the screen that owns it.
 */
export function CfoDashboard() {
  const org = useActiveOrg();
  const summary = useQuery({
    queryKey: ['cfo-summary', org?.id],
    queryFn: () => api.get<CfoSummary>('/cfo/summary'),
    enabled: !!org?.id,
  });
  const perms = usePermissions();
  const fyStartMonth = useFiscalYearStartMonth();
  const debts = useQuery({
    queryKey: ['cfo-debts', org?.id, '0'],
    queryFn: () => api.get<DebtView>('/cfo/debts'),
    enabled: !!org?.id && perms.can('banking', 'view'),
  });
  const asOf = summary.data?.asOf;
  const fyFrom = asOf ? fyStartOf(asOf, fyStartMonth) : undefined;
  const tds = useQuery({
    queryKey: ['cfo-tds', org?.id, fyFrom, asOf],
    queryFn: () => api.get<{ currency: string; totalDeducted: string; sections: Array<{ section: string; amount: string; count: number }> }>(`/withholding/tds${qs({ from: fyFrom, to: asOf })}`),
    enabled: !!org?.id && !!fyFrom && perms.can('payment', 'view'),
  });
  const spendFrom = asOf ? `${shiftMonth(asOf.slice(0, 7), -3)}-01` : undefined;
  const spending = useQuery({
    queryKey: ['cfo-spending', org?.id, spendFrom, asOf],
    queryFn: () => api.get<{ expense: CategoryRow[] }>(`/reports/categories${qs({ from: spendFrom, to: asOf, basis: 'cash', side: 'expense' })}`),
    enabled: !!org?.id && !!spendFrom && !!asOf,
  });

  if (summary.isError) {
    return (
      <Card>
        <ErrorState error={summary.error} onRetry={() => summary.refetch()} />
      </Card>
    );
  }
  if (summary.isLoading || !summary.data || !org) {
    return (
      <div className="space-y-4" aria-busy>
        <div className="skeleton h-28 rounded-lg" />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="skeleton h-24 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  const s = summary.data;
  const cur = s.currency;
  const minor = (value: string) => parse(value, cur).minor;

  return (
    <div className="space-y-6" data-cfo-dashboard>
      <AdviceStrip orgId={org.id} advice={s.advice} />

      <section aria-label="Cash position" className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {s.cash ? (
          <>
            <Tile
              label="Cash in bank and in hand"
              value={<Money value={s.cash.total} currency={cur} />}
              caption={
                s.cash.alsoHeld.length
                  ? `Also ${s.cash.alsoHeld.map((a) => `${a.currency} ${a.balance}`).join(', ')}, not added`
                  : s.cash.investments?.accounts.length
                    ? 'Cards, fixed deposits and other investments are not counted as cash'
                    : 'Cards are not counted as cash'
              }
            />
            <Tile label="Owed on cards" value={<Money value={s.cash.cardsOwed} currency={cur} />} caption="What was spent on cards and not yet paid off" />
            <Card>
              <CardContent className="space-y-2 pt-4">
                <p className="text-sm text-muted-foreground">How long the cash lasts</p>
                <p className="text-2xl font-semibold tabular-nums">{runwayText(s.runway?.ifNothingComesIn ?? null)}</p>
                <Meter
                  value={s.runway?.ifNothingComesIn ?? null}
                  max={12}
                  bands={[
                    [3, 'red'],
                    [6, 'amber'],
                    [12, 'green'],
                  ]}
                  unit="months"
                  label={`${runwayText(s.runway?.ifNothingComesIn ?? null)} if nothing came in`}
                />
                <p className="text-xs text-muted-foreground">
                  If nothing came in, at <Money value={s.runway?.monthlyOut ?? '0'} currency={cur} /> a month going out
                  {s.runway?.burning && s.runway.atThisPace !== null ? `; ${runwayText(s.runway.atThisPace)} at this pace` : ''}.
                </p>
              </CardContent>
            </Card>
            <Tile
              label="Cash in 13 weeks, expected"
              value={<Money value={s.forecast?.points.at(-1)?.expected ?? s.cash.total} currency={cur} />}
              caption={
                s.forecast ? (
                  <>
                    Cautious: <Money value={s.forecast.points.at(-1)?.cautious ?? '0'} currency={cur} />
                  </>
                ) : undefined
              }
            />
          </>
        ) : (
          <Card className="sm:col-span-2 lg:col-span-4">
            <CardContent className="py-4 text-sm text-muted-foreground">Cash, runway and the forecast need access to the bank accounts.</CardContent>
          </Card>
        )}
      </section>

      <DuesStrip dues={s.dues} currency={cur} />

      <div className="grid gap-4 lg:grid-cols-2">
        {s.forecast ? (
          <ChartCard title="Cash, the next 13 weeks" note="Expected, and cautious if late invoices never come in and the rest arrive a month later.">
            <SeriesChart
              labels={s.forecast.points.map((p) => shortDate(p.date))}
              longLabels={s.forecast.points.map((p) => longDate(p.date))}
              lines={[
                { id: 'expected', name: 'Expected', colour: null, values: s.forecast.points.map((p) => minor(p.expected)) },
                { id: 'cautious', name: 'Cautious', colour: 'amber', values: s.forecast.points.map((p) => minor(p.cautious)), dashed: true },
              ]}
              band={{ from: 'expected', to: 'cautious', colour: 'amber', label: 'Riding on late invoices' }}
              format={(v) => moneyText(v, cur)}
              tick={(v) => tickText(v, cur)}
              title="Cash, the next 13 weeks"
              summary={`Cash forecast from ${moneyText(minor(s.forecast.points[0]?.expected ?? '0'), cur)} today to ${moneyText(minor(s.forecast.points.at(-1)?.expected ?? '0'), cur)} expected in 13 weeks.`}
              empty={{ title: 'Nothing to forecast yet' }}
            />
            <details className="mt-3 text-xs text-muted-foreground">
              <summary className="cursor-pointer touch:min-h-11">How this is worked out</summary>
              <ul className="mt-2 list-disc space-y-1 pl-4">
                {s.forecast.assumptions.map((a) => (
                  <li key={a}>{a}</li>
                ))}
              </ul>
            </details>
          </ChartCard>
        ) : null}

        {s.waterfall ? (
          <ChartCard title={`Where the cash went, ${monthName(s.waterfall.month)}`} note="Bank and cash accounts, from the 1st to today.">
            <Waterfall
              openingLabel={`On the 1st`}
              openingMinor={minor(s.waterfall.opening)}
              steps={s.waterfall.steps.map((step) => ({ key: step.key, label: step.label, minor: minor(step.amount) }))}
              closingLabel="Today"
              currency={cur}
              title="Where the cash went this month"
              summary={`Cash went from ${moneyText(minor(s.waterfall.opening), cur)} on the 1st to ${moneyText(minor(s.waterfall.closing), cur)} today.`}
              empty={{ title: 'No money has moved this month' }}
            />
          </ChartCard>
        ) : null}

        <ChartCard title="What you are owed, by how late" link={{ to: '/invoices?status=unpaid', label: 'Unpaid invoices' }}>
          <HBars
            rows={(Object.keys(BUCKET_LABEL) as Bucket[]).map((b) => ({ id: b, name: BUCKET_LABEL[b], colour: BUCKET_COLOUR[b], value: minor(s.receivables.buckets[b]) }))}
            currency={cur}
            title="What you are owed, by how late"
            summary={`${moneyText(minor(s.receivables.total), cur)} owed, ${moneyText(minor(s.receivables.overdue), cur)} of it late.`}
            valueLabel="Owed"
            empty={{ title: 'Nobody owes you anything', description: 'Every invoice is paid.' }}
          />
        </ChartCard>

        <ChartCard title="Who owes you" link={{ to: '/customers', label: 'Customers' }}>
          <HBars
            rows={s.receivables.customers.slice(0, 8).map((c) => ({ id: c.customerId, name: c.name, colour: null, value: minor(c.total), part: minor(c.overdue) }))}
            currency={cur}
            title="Who owes you"
            summary={`${s.receivables.customers.length} customers owe you money.`}
            valueLabel="Owed"
            partLabel="Late"
            empty={{ title: 'Nobody owes you anything' }}
          />
        </ChartCard>

        {s.flows ? (
          <ChartCard title="Money in and out of the bank, 13 months" note="Transfers between your own accounts are left out.">
            <LineChart
              months={s.flows.months}
              lines={[
                { id: 'in', name: 'In', colour: 'green', values: s.flows.in.map(minor), width: 2 },
                { id: 'out', name: 'Out', colour: 'red', values: s.flows.out.map(minor), width: 2 },
              ]}
              currency={cur}
              title="Money in and out of the bank"
              summary="Money into and out of the bank, month by month."
              empty={{ title: 'No money has moved yet' }}
            />
          </ChartCard>
        ) : null}

        <ChartCard title="Days to get paid" note="From invoice to payment, averaged over what was paid each month.">
          <SeriesChart
            labels={s.collection.months.map((m) => monthName(m, true))}
            longLabels={s.collection.months.map((m) => monthName(m))}
            lines={[{ id: 'days', name: 'Days to get paid', colour: null, values: s.collection.days }]}
            format={(v) => `${v} days`}
            tick={(v) => `${v}d`}
            title="Days to get paid"
            summary="How many days customers took to pay, month by month."
            empty={{ title: 'No payments yet', description: 'Record a payment against an invoice and it shows here.' }}
          />
        </ChartCard>

        <ChartCard title="Where income comes from, 12 months" link={{ to: '/reports', label: 'Reports' }}>
          <Donut
            slices={s.concentration.customers.map((c, i) => ({
              key: c.customerId ?? 'rest',
              name: c.name,
              emoji: null,
              colour: c.customerId ? SLICE_COLOURS[i % SLICE_COLOURS.length] ?? 'slate' : 'slate',
              minor: minor(c.amount),
              share: c.share,
            })) satisfies DonutSlice[]}
            currency={cur}
            title="Income by customer, last 12 months"
            summary={`Income by customer over the last 12 months, ${moneyText(minor(s.concentration.total), cur)} in all.`}
            centreLabel="invoiced"
            empty={{ title: 'No invoices in the last 12 months' }}
          />
        </ChartCard>

        <ChartCard title="Where spending goes, 3 months" link={{ to: '/expenses', label: 'Expenses' }}>
          {spending.isError ? (
            <EmptyChart title="Spending could not be loaded" />
          ) : (
            <Donut
              slices={(spending.data?.expense ?? [])
                .filter((row) => !row.excluded && parse(row.amount, cur).minor > 0)
                .slice(0, 7)
                .map((row) => ({ key: row.categoryId ?? row.name, name: row.name, emoji: row.emoji, colour: row.colour, minor: minor(row.amount), share: row.share }))}
              currency={cur}
              title="Spending by category, last 3 months"
              summary="Spending by category over the last three months."
              centreLabel="out"
              empty={{ title: 'No spending in the last 3 months' }}
            />
          )}
        </ChartCard>

        {s.cash && s.cash.accounts.length > 0 ? (
          <ChartCard title="Each account" link={{ to: '/banking', label: 'Banking' }}>
            <HBars
              rows={s.cash.accounts.map((a) => ({ id: a.id, name: a.type === 'credit_card' ? `${a.name} (card)` : a.name, colour: a.type === 'credit_card' ? 'pink' : 'blue', value: minor(a.balance) }))}
              currency={cur}
              title="Balance in each account"
              summary="The balance in each account; a card below zero is owed."
              valueLabel="Balance"
              empty={{ title: 'No accounts yet' }}
            />
          </ChartCard>
        ) : null}

        {debts.data ? (
          <ChartCard title="What you owe, by rate" link={{ to: '/cfo?tab=debt', label: 'Debt' }}>
            <HBars
              rows={[...debts.data.debts]
                .sort((a, b) => Number(b.annualRate) - Number(a.annualRate))
                .map((d) => ({ id: d.id, name: `${d.lender} · ${Number(d.annualRate)}%`, colour: Number(d.annualRate) >= 30 ? 'red' : Number(d.annualRate) >= 15 ? 'amber' : 'teal', value: minor(d.outstanding) }))}
              currency={cur}
              title="What you owe, by lender and rate"
              summary={`${moneyText(minor(debts.data.totals.outstanding), cur)} owed, about ${moneyText(minor(debts.data.totals.monthlyInterest), cur)} a month in interest.`}
              valueLabel="Owed"
              empty={{ title: 'No debts entered', description: 'Add loans and cards on the CFO tab to see what they cost.' }}
            />
          </ChartCard>
        ) : null}

        {tds.data && tds.data.sections.length > 0 ? (
          <ChartCard title="TDS customers deducted, this year" note="Yours to claim once it shows in Form 26AS / AIS." link={{ to: '/payments', label: 'Payments' }}>
            <HBars
              rows={tds.data.sections.map((t) => ({ id: t.section, name: `${t.section} (${t.count})`, colour: 'violet', value: minor(t.amount) }))}
              currency={cur}
              title="TDS deducted by customers, by section"
              summary={`${moneyText(minor(tds.data.totalDeducted), cur)} deducted by customers this financial year.`}
              valueLabel="Deducted"
              empty={{ title: 'No TDS deducted' }}
            />
          </ChartCard>
        ) : null}
      </div>

      {s.receivables.late.length > 0 ? (
        <Card>
          <div className="flex items-center justify-between border-b border-border px-5 py-3">
            <h2 className="text-sm font-semibold">Late invoices, oldest first</h2>
            <Link to="/invoices?status=overdue" className="-my-2 flex min-h-9 items-center gap-1 text-sm text-primary hover:underline touch:min-h-11">
              All late <ArrowRight className="size-3.5" aria-hidden />
            </Link>
          </div>
          {/* A list, not a table: five columns do not fit a phone, and a row
              that says "22 days late, due 22 Sep" reads the same at any width. */}
          <ul className="divide-y divide-border">
            {s.receivables.late.map((row) => (
              <li key={row.id} className="flex items-center justify-between gap-3 px-5 py-3 text-sm">
                <div className="min-w-0">
                  <Link to={`/invoices/${row.id}`} className="font-medium text-primary hover:underline" data-tap-exempt="text link in a list row; the row is the hit area">
                    {row.number}
                  </Link>
                  <span className="text-muted-foreground"> · {row.customer}</span>
                  <p className="text-xs text-muted-foreground">
                    {row.daysLate} day{row.daysLate === 1 ? '' : 's'} late{row.dueDate ? `, due ${formatDate(row.dueDate)}` : ''}
                  </p>
                </div>
                <Money value={row.balance} currency={cur} className="shrink-0 font-medium tabular-nums" />
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      {s.exports.length > 0 ? (
        <Card>
          <div className="border-b border-border px-5 py-3">
            <h2 className="text-sm font-semibold">Exports waiting to be paid</h2>
            <p className="text-xs text-muted-foreground">Foreign money has to arrive within the FEMA period from the invoice date.</p>
          </div>
          <ul className="divide-y divide-border">
            {s.exports.map((e) => (
              <li key={e.id} className="flex flex-wrap items-center justify-between gap-2 px-5 py-3 text-sm">
                <Link to={`/invoices/${e.id}`} className="font-medium text-primary hover:underline touch:min-h-11">
                  {e.number}
                </Link>
                <span className="min-w-0 flex-1 truncate text-muted-foreground">{e.customer}</span>
                <span className="tabular-nums">
                  {e.currency} {e.balance}
                </span>
                <span className={e.daysLeft < 30 ? 'text-destructive' : 'text-muted-foreground'}>
                  {e.daysLeft < 0 ? `${-e.daysLeft} days past ${formatDate(e.dueDate)}` : `${e.daysLeft} days left, by ${formatDate(e.dueDate)}`}
                </span>
              </li>
            ))}
          </ul>
        </Card>
      ) : null}

      <p className="flex items-start gap-2 text-xs text-muted-foreground">
        <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden />
        Worked out from your books as of {formatDate(s.asOf)}. Information and calculations, not investment, tax or legal advice; confirm dates and rates with your CA.
      </p>
    </div>
  );
}

function ChartCard({ title, note, link, children }: { title: string; note?: string; link?: { to: string; label: string }; children: React.ReactNode }) {
  return (
    <Card className="min-w-0">
      <CardContent className="space-y-3 pt-4">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold">{title}</h2>
            {note ? <p className="text-xs text-muted-foreground">{note}</p> : null}
          </div>
          {link ? (
            <Link to={link.to} className="-my-1 flex shrink-0 items-center justify-end gap-1 text-xs text-primary hover:underline touch:min-h-11 touch:min-w-11">
              {link.label} <ArrowRight className="size-3" aria-hidden />
            </Link>
          ) : null}
        </div>
        {children}
      </CardContent>
    </Card>
  );
}

/** The statutory dues ahead, as a strip of dates: what is late, what is close, and what is coming. */
function DuesStrip({ dues, currency }: { dues: DueView[]; currency: string }) {
  if (dues.length === 0) return null;
  return (
    <Card data-cfo-dues-strip>
      <div className="flex items-center justify-between border-b border-border px-5 py-3">
        <h2 className="text-sm font-semibold">Due in the next 45 days</h2>
        <Link to="/cfo?tab=dues" className="-my-2 flex min-h-9 items-center gap-1 text-sm text-primary hover:underline touch:min-h-11">
          Calendar <ArrowRight className="size-3.5" aria-hidden />
        </Link>
      </div>
      <ul className="flex gap-3 overflow-x-auto px-5 py-3">
        {dues.slice(0, 10).map((d) => (
          <li
            key={d.key}
            className={
              d.status === 'overdue'
                ? 'min-w-40 shrink-0 rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs'
                : d.status === 'soon'
                  ? 'min-w-40 shrink-0 rounded-md border border-warning/40 bg-warning/5 px-3 py-2 text-xs'
                  : 'min-w-40 shrink-0 rounded-md border border-border px-3 py-2 text-xs'
            }
          >
            <p className="font-semibold">{formatDate(d.date)}</p>
            <p>{d.title}</p>
            <p className="text-muted-foreground">{d.period}</p>
            {d.estimate ? (
              <p>
                about <Money value={d.estimate} currency={currency} />
              </p>
            ) : null}
          </li>
        ))}
      </ul>
    </Card>
  );
}

function fyStartOf(date: string, startMonth: number): string {
  const y = Number(date.slice(0, 4));
  const m = Number(date.slice(5, 7));
  const year = m >= startMonth ? y : y - 1;
  return `${year}-${String(startMonth).padStart(2, '0')}-01`;
}

function runwayText(months: number | null): string {
  if (months === null) return 'No spending yet';
  if (months < 1) return 'Under a month';
  return `${months} month${months === 1 ? '' : 's'}`;
}

function shiftMonth(ym: string, by: number): string {
  const [y, m] = ym.split('-').map(Number);
  const d = new Date(Date.UTC(y ?? 2000, (m ?? 1) - 1 + by, 1));
  return d.toISOString().slice(0, 7);
}

function monthName(ym: string, short = false): string {
  const d = new Date(`${ym}-01T00:00:00Z`);
  return new Intl.DateTimeFormat('en-IN', short ? { month: 'short', timeZone: 'UTC' } : { month: 'long', year: 'numeric', timeZone: 'UTC' }).format(d);
}

function shortDate(iso: string): string {
  return new Intl.DateTimeFormat('en-IN', { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(new Date(`${iso}T00:00:00Z`));
}

function longDate(iso: string): string {
  return new Intl.DateTimeFormat('en-IN', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${iso}T00:00:00Z`));
}
